// src/components/strumenti/RataFinanziamento.js
// Rata costante (ammortamento alla francese): R = C × [i(1+i)^n] / [(1+i)^n − 1]
import React, { useState } from "react";
import { RefreshCw, Printer } from "lucide-react";
import { leggiNumero } from "../../utils/numeriStrumenti";

const inp = { padding:"10px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:14, fontFamily:"'Inter',sans-serif", outline:"none", width:"100%" };
const lbl = { fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 };

export default function RataFinanziamento() {
  const [denominazione, setDenominazione] = useState("");
  const [note, setNote] = useState("");
  const [capitale, setCapitale] = useState("");
  const [tassoAnnuo, setTassoAnnuo] = useState("");
  const [durataMesi, setDurataMesi] = useState("36");

  const C = leggiNumero(capitale)||0;
  const tassoAnno = leggiNumero(tassoAnnuo)||0;
  const n = parseInt(durataMesi)||0;
  const i = tassoAnno/100/12; // tasso mensile

  const puoCalcolare = C>0 && n>0;
  let rata=null, totalePagato=null, totaleInteressi=null;
  if (puoCalcolare) {
    if (i>0) {
      rata = C*(i*Math.pow(1+i,n))/(Math.pow(1+i,n)-1);
    } else {
      rata = C/n; // tasso zero
    }
    totalePagato = rata*n;
    totaleInteressi = totalePagato-C;
  }

  const stampaPDF = () => {
    const win = window.open("", "_blank");
    const oggi = new Date().toLocaleDateString("it-IT", { day:"numeric", month:"long", year:"numeric" });
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Rata Finanziamento — ${denominazione}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;color:#111;padding:14mm;font-size:12px;}
      @media screen{body{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4;margin:14mm;}
      h1{font-size:18px;color:#059669;margin-bottom:2px;}
      .sub{font-size:11px;color:#6b7280;margin-bottom:16px;}
      table{width:100%;border-collapse:collapse;margin-bottom:14px;}
      td,th{border:1px solid #e5e7eb;padding:6px 10px;text-align:left;font-size:12px;}
      th{background:#f9fafb;width:45%;color:#374151;}
      .risultato{background:#ecfdf5;border:1px solid #a7f3d0;border-radius:8px;padding:16px;text-align:center;margin-bottom:14px;}
      .risultato .val{font-size:28px;font-weight:800;color:#059669;}
    </style></head><body>
      <h1>💳 Rata Finanziamento / Leasing</h1>
      <div class="sub">Nexiora — Strumenti Tecnici · Generato il ${oggi}</div>
      <table>
        <tr><th>Denominazione</th><td>${denominazione || "—"}</td></tr>
        <tr><th>Note</th><td>${note || "—"}</td></tr>
      </table>
      <table>
        <tr><th>Capitale finanziato</th><td>${capitale} €</td></tr>
        <tr><th>Tasso annuo (TAN)</th><td>${tassoAnnuo}%</td></tr>
        <tr><th>Durata</th><td>${durataMesi} mesi</td></tr>
      </table>
      <div class="risultato">
        <div style="font-size:11px;color:#9ca3af;margin-bottom:4px;">Rata mensile</div>
        <div class="val">${rata!==null ? rata.toLocaleString("it-IT",{minimumFractionDigits:2,maximumFractionDigits:2}) : "—"} €</div>
        <div style="font-size:12px;color:#6b7280;margin-top:8px;">Totale pagato: ${totalePagato!==null?totalePagato.toLocaleString("it-IT",{maximumFractionDigits:2}):"—"} € · Totale interessi: ${totaleInteressi!==null?totaleInteressi.toLocaleString("it-IT",{maximumFractionDigits:2}):"—"} €</div>
      </div>
      <div style="font-size:10px;color:#9ca3af;">Rata a quota costante — non include spese di istruttoria, assicurazioni o altri oneri accessori spesso presenti nei finanziamenti reali.</div>
      
    </body></html>`);
    win.document.close();
    setTimeout(()=>{
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
  };

  return (
    <div>
      <div style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:8, padding:12, marginBottom:16 }}>
        <div style={{ marginBottom:8 }}>
          <label style={lbl}>Denominazione (necessaria per la stampa)</label>
          <input value={denominazione} onChange={e=>setDenominazione(e.target.value)} style={inp} placeholder="Es. Finanziamento furgone Iveco Daily"/>
        </div>
        <div>
          <label style={lbl}>Note (facoltativo)</label>
          <input value={note} onChange={e=>setNote(e.target.value)} style={inp} placeholder="Es. Preventivo Banca XY"/>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
        <div>
          <label style={lbl}>Capitale finanziato (€)</label>
          <input value={capitale} onChange={e=>setCapitale(e.target.value)} style={inp} placeholder="Es. 20000"/>
        </div>
        <div>
          <label style={lbl}>Tasso annuo (TAN %)</label>
          <input value={tassoAnnuo} onChange={e=>setTassoAnnuo(e.target.value)} style={inp} placeholder="Es. 6.5"/>
        </div>
      </div>
      <div style={{ marginBottom:16 }}>
        <label style={lbl}>Durata (mesi)</label>
        <input value={durataMesi} onChange={e=>setDurataMesi(e.target.value)} style={inp} placeholder="Es. 36"/>
      </div>

      <div style={{ background:"#ecfdf5", border:"1px solid #a7f3d0", borderRadius:10, padding:"14px 16px" }}>
        <div style={{ textAlign:"center", marginBottom:10 }}>
          <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, marginBottom:2 }}>Rata mensile</div>
          <div style={{ fontSize:26, fontWeight:800, color:"#059669" }}>{rata!==null ? rata.toLocaleString("it-IT",{minimumFractionDigits:2,maximumFractionDigits:2})+" €" : "—"}</div>
        </div>
        <div style={{ display:"flex", justifyContent:"space-around", borderTop:"1px solid #a7f3d0", paddingTop:10 }}>
          <div style={{textAlign:"center"}}>
            <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>Totale pagato</div>
            <div style={{ fontSize:15, fontWeight:700, color:"#374151" }}>{totalePagato!==null ? totalePagato.toLocaleString("it-IT",{maximumFractionDigits:2}) : "—"} €</div>
          </div>
          <div style={{textAlign:"center"}}>
            <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600 }}>Totale interessi</div>
            <div style={{ fontSize:15, fontWeight:700, color:"#374151" }}>{totaleInteressi!==null ? totaleInteressi.toLocaleString("it-IT",{maximumFractionDigits:2}) : "—"} €</div>
          </div>
        </div>
      </div>
      <div style={{ fontSize:10, color:"#9ca3af", marginTop:8, textAlign:"center" }}>
        Rata a quota costante (ammortamento alla francese) — non include spese di istruttoria, assicurazioni o altri oneri accessori spesso presenti nei finanziamenti reali.
      </div>

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:12 }}>
        <button onClick={()=>{setCapitale("");setTassoAnnuo("");setDurataMesi("36");setDenominazione("");setNote("");}}
          style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#9ca3af", background:"none", border:"none", cursor:"pointer" }}>
          <RefreshCw size={12}/> Azzera
        </button>
        <button onClick={stampaPDF} disabled={!denominazione.trim()}
          title={!denominazione.trim() ? "Inserisci la denominazione per stampare" : "Stampa PDF"}
          style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:700,
            color: denominazione.trim() ? "#059669" : "#a7f3d0",
            background: denominazione.trim() ? "#ecfdf5" : "#f9fafb",
            border:`1px solid ${denominazione.trim() ? "#a7f3d0" : "#e5e7eb"}`,
            borderRadius:8, padding:"7px 14px", cursor: denominazione.trim() ? "pointer" : "not-allowed" }}>
          <Printer size={13}/> Stampa PDF
        </button>
      </div>
    </div>
  );
}
